import { createSlice } from '@reduxjs/toolkit';
// utils
import { callApi } from '../../utils/apiCall';
//
import { dispatch, store } from '../store';
import { reset as taskReset } from './taskSlice';
import { reset as taskTemplateReset } from './taskTemplateSlice';
import { reset as taskNotificationReset } from './taskNotificationSlice';

// ---------------------------------------------------------------------
const storedPracticeId = localStorage.getItem('practiceId');
const validPracticeId = (storedPracticeId && storedPracticeId !== 'null' && storedPracticeId !== 'undefined')
    ? storedPracticeId : null;

// Hydrate the "All Practices" multi-select from localStorage. Shape is
// an array of integer ids, or null to mean "use the single primary
// practiceId below". We validate aggressively because a stale/bogus
// array would send bad `practice__in` payloads to every page — the
// cost of strict validation here is a fresh hydrate on next selection,
// which the user notices as "my selection didn't stick" and re-picks.
// That's a better failure mode than silent 403s behind every feature.
const _hydrateActivePracticeIds = () => {
    try {
        const raw = localStorage.getItem('activePracticeIds');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed) || parsed.length === 0) return null;
        const ints = parsed.map(Number).filter((n) => Number.isFinite(n) && n > 0);
        return ints.length ? ints : null;
    } catch {
        return null;
    }
};

const initialState = {
    isLoading: false,
    practiceTreatment: {
        results: []
    },
    practiceId: validPracticeId,
    practiceIdToChange: validPracticeId,
    // Phase-1 of the All Practices rollout. When null the app behaves
    // exactly as before — everything scopes to `practiceId`. When a
    // non-empty array, callers that opt in (via useActivePracticeIds)
    // aggregate across those practices via `?practice__in=...`.
    // Backward compat: pages that haven't been migrated to the hook
    // still read `practiceId` and show the single-practice view.
    activePracticeIds: _hydrateActivePracticeIds(),
    // Monotonic counter bumped ONLY by a user-initiated practice switch from
    // the header switcher. The dashboard layout keys <Outlet> on it so the
    // routed page gets a genuine unmount/remount even when the switch keeps
    // the user on the same route. Before this, every switch hard-navigated to
    // /dashboard, and that navigation was what remounted the page — pages that
    // fetch only on mount would otherwise sit there showing the PREVIOUS
    // practice's data (or nothing, since the switch clears most slices).
    // Deliberately NOT bumped by switchPractice(): several pages
    // (Whatsapp, LeadDetail, PracticeDetails) call it to sync Redux to a
    // practice id already present in the URL, and remounting them mid-sync
    // would double-fetch the record they just opened.
    practiceSwitchEpoch: 0,
    // { practiceId, pathname } of the page a user-initiated practice switch
    // just landed on, held until the new practice's permissions have been
    // reconciled against that page (see DashboardHeader). It lives in Redux
    // rather than component state because <FeatureGuard> also needs it: when a
    // guard denies access on the very path a switch just landed on, that is a
    // routine "the new practice doesn't have this feature" outcome and should
    // fall back to the dashboard, not to the /permission-denied dead end.
    pendingSwitchLanding: null,
};


const slice = createSlice({
    name: 'practiceTreatmentList',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        // START LOADING
        startLoading(state) {
            state.isLoading = true;
        },
        getPracticeTreatmentData(state, action) {
            state.isLoading = false;
            state.practiceTreatment = action.payload;
        },
        addMorePracticeTreatmentData(state, action) {
            state.isLoading = false;
            state.practiceTreatment.results = [
                ...state.practiceTreatment.results,
                ...action.payload
            ];
        },
        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        updatePracticeId(state, action) {
            localStorage.setItem('practiceId', action.payload);
            state.practiceId = action.payload;
        },
        // See `practiceSwitchEpoch` in initialState.
        bumpPracticeSwitchEpoch(state) {
            state.practiceSwitchEpoch += 1;
        },
        // See `pendingSwitchLanding` in initialState.
        setPendingSwitchLanding(state, action) {
            state.pendingSwitchLanding = action.payload || null;
        },
        clearPendingSwitchLanding(state) {
            state.pendingSwitchLanding = null;
        },
        updatePracticeIdToChange(state, action) {
            localStorage.setItem('practiceId', action.payload);
            state.practiceIdToChange = action.payload;
        },
        // Set (or clear) the active-practice multi-select. Payload is an
        // array of ids, or null to "clear back to single-practice mode".
        // Writes through to localStorage so the selection survives a
        // reload — same pattern reportPracticeIds uses today.
        setActivePracticeIds(state, action) {
            const { payload } = action;
            if (payload == null || (Array.isArray(payload) && payload.length === 0)) {
                state.activePracticeIds = null;
                try { localStorage.removeItem('activePracticeIds'); } catch { /* quota / private-mode */ }
                return;
            }
            const ints = (Array.isArray(payload) ? payload : [payload])
                .map(Number)
                .filter((n) => Number.isFinite(n) && n > 0);
            state.activePracticeIds = ints.length ? ints : null;
            try {
                if (ints.length) localStorage.setItem('activePracticeIds', JSON.stringify(ints));
                else localStorage.removeItem('activePracticeIds');
            } catch {
                // localStorage unavailable (private mode, quota) — state
                // still updates in-memory so the session works; selection
                // just doesn't persist across reload. Acceptable.
            }
        },
    }
});

// Reducer
export default slice.reducer;
export const {
    updatePracticeId,
    reset,
    getPracticeTreatmentData,
    updatePracticeIdToChange,
    setActivePracticeIds,
    bumpPracticeSwitchEpoch,
    setPendingSwitchLanding,
    clearPendingSwitchLanding,
} = slice.actions;

// Centralised practice switch: clears V2 task slices so data from the previous
// practice doesn't leak into the new practice's task surface, then updates
// practiceId. Every user-facing practice-change call site should use this.
// Direct `updatePracticeId` dispatches are reserved for logout/null-clearing
// paths that bypass the V2 task reset.
export function switchPractice(practiceId) {
    return async () => {
        dispatch(taskReset());
        dispatch(taskTemplateReset());
        dispatch(taskNotificationReset());
        dispatch(slice.actions.updatePracticeId(practiceId));
    };
}

export function getPracticeTreatmentListData(callback) {
    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await callApi('/api/v1/practices/?ordering=name&fields={id,name,logo}');
            const { practiceId } = store.getState().practiceTreatmentList;
            if (response.status === 200) {
                dispatch(slice.actions.getPracticeTreatmentData(response?.data));
                if ((response.data && response.data.results.length > 0) && (!practiceId || practiceId === 'null' || practiceId === 'undefined')) {
                    const PracticeId = response.data.results[0].id;
                    dispatch(slice.actions.updatePracticeId(PracticeId));
                }
                if (response.data && response.data.results && response.data.results.length <= 0) {
                    callback();
                }
            } else {
                dispatch(slice.actions.hasError(response.detail || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}